import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Circle } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnread(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const typeIcons = {
    referral: '👥',
    parlay: '🎯',
    subscription: '⭐',
    pick: '🔥',
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors" data-testid="notification-bell">
          <Bell size={20} weight={unread > 0 ? 'fill' : 'regular'} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#FF6B1A] rounded-full text-[9px] font-bold text-white flex items-center justify-center" data-testid="notif-count">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#111115] border-white/10 w-80 max-h-96 overflow-y-auto p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <span className="font-heading font-bold text-sm uppercase text-white">NOTIFICATIONS</span>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-[10px] text-[#FF6B1A] hover:text-[#FF853E] font-heading font-bold uppercase" data-testid="mark-all-read">
              MARK ALL READ
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell size={28} className="text-white/10 mx-auto mb-2" />
            <p className="text-xs text-white/30">No notifications yet</p>
          </div>
        ) : (
          notifications.slice(0, 15).map((n) => (
            <div
              key={n.id}
              className={`px-4 py-3 border-b border-white/5 ${!n.read ? 'bg-white/[0.02]' : ''}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <Circle size={6} weight="fill" className="text-[#FF6B1A] mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white">{n.title}</p>
                  <p className="text-[10px] text-white/40 mt-0.5">{n.message}</p>
                  <p className="text-[9px] text-white/20 mt-1">
                    {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
